import React, { useContext, useState, useRef, useCallback, useEffect } from "react";
import { NavLink } from "react-router-dom";

//pages & includes
import ManageSidebar from "../ManageSidebar";

//functions
import { _t, getCookie, modalLoading } from "../../../../../functions/Functions";
import { useTranslation } from "react-i18next";

import { BASE_URL } from "../../../../../BaseUrl";
import axios from "axios";


//3rd party packages
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import { MdDelete } from 'react-icons/md';
import Switch from "react-switch";

//context consumer
import { SettingsContext } from "../../../../../contexts/Settings";
import { confirmAlert } from "react-confirm-alert";
import CustomSpinLoader from "../../../../components/CustomSpinLoader";
import DataTable from "../../../../common/DataTable";

const CurrencyNoteCrud = () => {
  // 3rd party hooks
  const { t } = useTranslation();

  // contexts
  const {
    //common
    loading,
    setLoading,
    currencyNoteList,
    setCurrencyNoteList,
    currencyNoteForSearch,
    setCurrencyNoteForSearch,
    setPaginatedCurrencyNote,
    dataPaginating,
  } = useContext(SettingsContext);

  // states
  const [searchedCurrencyNotes, setSearchedCurrencyNotes] = useState({
    list: [],
    searched: false,
  });

  const searchInputRef = useRef(null);

  const handleSearch = (e) => {
    const searchTerm = e.target.value.trim().toLowerCase();

    if (!searchTerm) {
      return setSearchedCurrencyNotes({ list: [], searched: false });
    }

    const searchableKeys = ["name"];

    const newList = (currencyNoteForSearch || []).filter((item) =>
      searchableKeys.some((key) => {
        if (!item || item[key] == null) return false;
        return item[key].toString().toLowerCase().includes(searchTerm);
      })
    );

    setSearchedCurrencyNotes({
      list: newList,
      searched: true,
    });
  };

  useEffect(() => {
    if(setPaginatedCurrencyNote){
      setPaginatedCurrencyNote(1)
    }
  }, [])

  const setNewData = (res) => {
    setCurrencyNoteList(res.data[0]);
    setCurrencyNoteForSearch(res.data[1]);
    setSearchedCurrencyNotes({
      searched: false,
      list: [],
    });
    setLoading(false);
  }

  const apiFailedToast = () => {
    setLoading(false);
    toast.error(`${_t(t("Please try again"))}`, {
      position: "bottom-center",
      autoClose: 10000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      className: "text-center toast-notification",
    });
  }

  // DELETE
  const handleDeleteConfirmation = (id) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="card card-body">
            <h1>{_t(t("Are you sure?"))}</h1>
            <p className="text-center">{_t(t("You want to delete this?"))}</p>
            <div className="d-flex justify-content-center">
              <button
                className="btn btn-primary rounded"
                onClick={() => {
                  handleDeleteCurrencyNote(id);
                  onClose();
                }}
              >
                {_t(t("Yes, delete it!"))}
              </button>
              <button className="btn btn-success ml-2 px-3 rounded" onClick={onClose}>
                {_t(t("No"))}
              </button>
            </div>
          </div>
        );
      },
    });
  }
  const handleDeleteCurrencyNote = async (id) => {
    setLoading(true);
    const deleteUrl = BASE_URL + `/settings/cash-note-types/${id}`
    return axios.delete(
      deleteUrl, {
      headers: { Authorization: `Bearer ${getCookie()}` },
    })
      .then(res => {
        setNewData(res)
        toast.success(`${_t(t("Currency Note has been deleted successfully"))}`, {
          position: "bottom-center",
          autoClose: 10000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          className: "text-center toast-notification",
        });
      })
      .catch((err) => {
        setLoading(false);
        if (err.response?.data?.status === 0) {
          toast.error(`${_t(t(err.response.data.message))}`, {
            position: "bottom-center",
            autoClose: 10000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            className: "text-center toast-notification",
          });
          return
        }
        apiFailedToast()
      })
  }

  const handleFetchData = (page) => {
    setPaginatedCurrencyNote(page);
  };

  const COLUMNS = [
    { label: "S/L", render: (item, i) => i + 1 + ((currencyNoteList?.current_page || 1) - 1) * (currencyNoteList?.per_page || 10) },
    { label: "Name", render: (item) => item.name },
    { label: "Rate", render: (item) => item.value },
    {
      label: "Status",
      render: (item) => (
        <div className="d-flex justify-content-center">
          <Switch
            onChange={() => {}}
            checked={item.status === 1}
            disabled={true}
            height={20}
            width={40}
          />
        </div>
      ),
    },
    {
      label: "Action",
      render: (item) => (
        <div className="d-flex justify-content-center">
          <NavLink
            to={`/dashboard/manage/settings/currency-note-edit/${item.id}`}
            className="btn btn-primary ml-2 rounded-sm"
            title="Edit"
            style={{ height: "1.7rem", width: "1.7rem" }}
          >
            <div className="d-flex align-items-center justify-content-center">
              <i className="fa fa-pencil edit-icon" aria-hidden="true"></i>
            </div>
          </NavLink>
          <button
            title="Delete"
            className="btn btn-danger ml-2 rounded-sm del-btn"
            onClick={() => handleDeleteConfirmation(item.id)}
          >
            <MdDelete className="del-icon" />
          </button>
        </div>
      ),
    },
  ];

  const getListData = useCallback(() => {
    if (searchedCurrencyNotes.searched) {
      return searchedCurrencyNotes.list;
    }
    return currencyNoteList?.data || [];
  }, [searchedCurrencyNotes, currencyNoteList]);
  
  const onClearSearch = () => {
    setSearchedCurrencyNotes({ ...searchedCurrencyNotes, searched: false });
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
  };

  return (
    <>
      <Helmet>
        <title>{_t(t("Currency Note List"))}</title>
      </Helmet>

      {/* main body */}
      <main id="main">
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            {/* left Sidebar */}
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ManageSidebar />
            </div>

            {/* main content */}
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div key="fragment3">
                {/* top search */}
                <div className="t-bg-white">
                  <div className="row mx-2 gx-2 align-items-center t-pt-15 t-pb-15">
                    <div className="col-md-6 col-lg-5 t-mb-15 mb-md-0">
                      <ul className="t-list fk-breadcrumb d-flex justify-content-between align-items-center">
                        <li className="fk-breadcrumb__list">
                          <span className="t-link fk-breadcrumb__link text-capitalize">
                            {_t(t("Currency Note List"))}
                          </span>
                        </li>
                      </ul>
                    </div>
                    <div className="col-md-6 col-lg-7">
                      <div className="row gx-3 align-items-center">
                        {/* Search group */}
                        <div className="col-md-3"></div>
                        <div className="col-md-6 t-mb-15 mb-md-0">
                          <div className="input-group">
                            <div className="form-file">
                              <input
                                type="text"
                                className="form-control border-0 form-control--light-1 rounded-left"
                                placeholder={_t(t("Search by name")) + ".."}
                                onChange={handleSearch}
                                disabled={loading}
                                ref={searchInputRef}
                              />
                            </div>
                            <button
                              className="btn btn-primary rounded-right"
                              type="button"
                            >
                              <i
                                className="fa fa-search"
                                aria-hidden="true"
                              ></i>
                            </button>
                          </div>
                        </div>

                        {/* Add group modal trigger button */}
                        <div className="col-md-3 text-md-right">
                           <NavLink
                            to="/dashboard/manage/settings/currency-note-add"
                            className="btn btn-primary xsm-text text-uppercase btn-lg btn-block rounded"
                          >
                            {_t(t("add new"))}
                          </NavLink>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Table */}
                <DataTable
                  columns={COLUMNS}
                  data={getListData()}
                  emptyText="No data available"
                  isLoading={loading}
                  pagination={{
                    isPageLoading: loading,
                    isPagination: true,
                    pageCount: currencyNoteList?.last_page,
                    currentPage: currencyNoteList?.current_page,
                    from: currencyNoteList?.from,
                    to: currencyNoteList?.to,
                    total: currencyNoteList?.total,
                  }}
                  handleFetchData={handleFetchData}
                  searchPayload={{
                    isSearched: searchedCurrencyNotes.searched,
                    onClearSearch,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default CurrencyNoteCrud;
